/**
 * AlphaCore service worker registration + PWA install prompt hook.
 * Runs only in production builds — SW registration is guarded by env + isSecureContext.
 */
import { useEffect, useState } from "react";

let deferredInstallPrompt = null;
const installListeners = new Set();
let isStandalone = false;
let updateReady = false;
const updateListeners = new Set();

function detectStandalone() {
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator?.standalone === true
    );
  } catch { return false; }
}

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  isStandalone = detectStandalone();

  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    installListeners.forEach((cb) => cb(true));
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    installListeners.forEach((cb) => cb(false));
  });

  if (!("serviceWorker" in navigator)) return;
  if (!window.isSecureContext) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").then((reg) => {
      // If there's already a waiting worker, we have an update ready
      if (reg.waiting) { updateReady = true; updateListeners.forEach((cb) => cb(true)); }
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            updateReady = true;
            updateListeners.forEach((cb) => cb(true));
          }
        });
      });
    }).catch(() => { /* ignore */ });

    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}

export function useInstallPrompt() {
  const [available, setAvailable] = useState(!!deferredInstallPrompt);
  const [standalone] = useState(detectStandalone());
  useEffect(() => {
    const cb = (v) => setAvailable(v);
    installListeners.add(cb);
    return () => installListeners.delete(cb);
  }, []);
  const promptInstall = async () => {
    if (!deferredInstallPrompt) return "unavailable";
    const evt = deferredInstallPrompt;
    deferredInstallPrompt = null;
    evt.prompt();
    const { outcome } = await evt.userChoice.catch(() => ({ outcome: "dismissed" }));
    setAvailable(false);
    return outcome;
  };
  return { available, standalone, promptInstall };
}

export function useUpdateReady() {
  const [ready, setReady] = useState(updateReady);
  useEffect(() => {
    const cb = (v) => setReady(v);
    updateListeners.add(cb);
    return () => updateListeners.delete(cb);
  }, []);
  const applyUpdate = async () => {
    if (!("serviceWorker" in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    reg?.waiting?.postMessage({ type: "SKIP_WAITING" });
  };
  return { ready, applyUpdate };
}
